import React, { useEffect, useState } from "react";
import { Col, Placeholder, Row, Toast, ToastContainer } from "react-bootstrap";
import { Helmet } from "react-helmet";
import { useNavigate, useParams } from "react-router-dom";

import DstButton from "../../components/DstButton/DstButton";
import DstTabs from "../../components/DstTabs/DstTabs";
import { useSession } from "../../contexts/SessionContext";
import ShortcutService from "../../services/ShortcutService";
import { formatDateHumanly } from "../../services/TimeService";
import translate from "../../services/Translate";
import { completeNavigate } from "../../services/UtilityService";
import { fetchProjectDetails, fetchProjectMeetings, fetchProjectUsers } from "./Project.function";
// import Attendance from "./components/Attendance/Attendance";
// import Comments from "./components/Comments/Comments";
import MainInfo from "./components/MainInfos/MainInfo";
import Notes from "./components/Notes/Notes";

const Project = () => {
  const { project_id } = useParams();
  const { language } = useSession();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("main-infos");
  const [project, setProject] = useState<any>(null);
  const [isLoadedGeneralInfo, setIsLoadedGeneralInfo] = useState(false);
  const [meetings, setMeetings] = useState<any[]>([]);
  const [isLoadedMeetings, setIsLoadedMeetings] = useState(false);
  const [users, setUsers] = useState<any[]>([]);
  const [isLoadedUsers, setIsLoadedUsers] = useState(false);
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [showCopyShortcutToast, setShowCopyShortcutToast] = useState(false);

  useEffect(() => {
    if (!project_id) return;

    fetchProjectDetails(Number(project_id), setProject, setIsLoadedGeneralInfo, language);
    fetchProjectMeetings(Number(project_id), setMeetings, setIsLoadedMeetings, language);
    fetchProjectUsers(Number(project_id), setUsers, setIsLoadedUsers, language);
  }, [project_id, language]);

  useEffect(() => {
    const copyProjectToClipboard = () => {
      if (!project?.name || !project?.dt_end) return;

      const mentor = project.overseer?.full_name ? ` (${project.overseer.full_name})` : "";
      const endDate = formatDateHumanly(language, project.dt_end);
      const projectInfo = `${project.name} | ${window.location.href} | ${endDate}${mentor}`;

      navigator.clipboard
        .writeText(projectInfo)
        .then(() => setShowCopyShortcutToast(true))
        .catch((error) => console.error("Failed to copy project info to clipboard:", error));
    };

    const listenDestructor = ShortcutService.listen("c", copyProjectToClipboard, false, true);
    return () => listenDestructor();
  }, [language, project]);

  if (!isLoadedGeneralInfo) {
    return (
      <div className="container-fluid mt-3 mb-5 px-3 px-md-4">
        <Helmet>
          <title>Project - {project_id}</title>
        </Helmet>

        <div className="d-flex align-items-center mb-3">
          <span className="dst-icon-chevron-left p-2 medium-gray-background rounded me-2" />

          <Placeholder as="h1" animation="glow" className="mb-0 h3 me-3 w-50">
            <Placeholder xs={6} />
          </Placeholder>
        </div>

        <div className="d-flex gap-2 mb-3">
          {[...Array(3)].map((_, index) => (
            <Placeholder
              key={`project-mocking-line-${index}`}
              as="div"
              animation="glow"
              className="rounded"
              style={{ width: 140, height: 38 }}
            >
              <Placeholder xs={12} />
            </Placeholder>
          ))}
        </div>

        <Row>
          <Col>
            <Placeholder as="div" animation="glow">
              <Placeholder xs={12} className="mb-2" />
              <Placeholder xs={11} className="mb-2" />
              <Placeholder xs={10} className="mb-2" />
              <Placeholder xs={8} />
            </Placeholder>
          </Col>
          <Col>
            <Placeholder as="div" animation="glow">
              <Placeholder xs={12} className="mb-2" />
              <Placeholder xs={11} className="mb-2" />
              <Placeholder xs={10} className="mb-2" />
              <Placeholder xs={8} />
            </Placeholder>
          </Col>
        </Row>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="text-center mt-3">
        <h2>{translate(language, "PAGES.PROJECT.ERROR.NOT_FOUND")}</h2>
        <DstButton
          value={translate(language, "PAGES.PROJECT.BUTTONS.ALL_PROJECTS_TITLE")}
          clickFunction={() => navigate("/projects")}
        />
      </div>
    );
  }

  const tabsData = [
    {
      id: "main-infos",
      title: (
        <React.Fragment>
          <span className="dst-icon-settings-04 me-2" />
          {translate(language, "PAGES.PROJECT.TABS.MAIN_INFOS")}
        </React.Fragment>
      ),
      content: (
        <MainInfo
          project={project}
          meetings={meetings}
          loadingMeetings={!isLoadedMeetings}
          users={users}
          loadingUsers={!isLoadedUsers}
          refreshUsers={() => fetchProjectUsers(Number(project_id), setUsers, setIsLoadedUsers, language)}
          refreshMeetings={() => fetchProjectMeetings(Number(project_id), setMeetings, setIsLoadedMeetings, language)}
          onProjectEditSuccess={() => {
            fetchProjectDetails(Number(project_id), setProject, setIsLoadedGeneralInfo, language);
            setShowSuccessToast(true);
          }}
        />
      ),
    },
    {
      id: "notes",
      title: (
        <React.Fragment>
          <span className="dst-icon-annotation me-2" />
          {translate(language, "PAGES.PROJECT.TABS.NOTES")}
        </React.Fragment>
      ),
      content: <Notes />,
    },
    // {
    //   id: "attendance",
    //   title: (
    //     <React.Fragment>
    //       <span className="dst-icon-clipboard-check me-2" />
    //       {translate(language, "PAGES.PROJECT.TABS.ATTENDANCE")}
    //     </React.Fragment>
    //   ),
    //   content: <Attendance />,
    // },
    // {
    //   id: "comments",
    //   title: (
    //     <React.Fragment>
    //       <span className="dst-icon-star-01 me-2" />
    //       {translate(language, "PAGES.PROJECT.TABS.COMMENTS")}
    //     </React.Fragment>
    //   ),
    //   content: <Comments />,
    // },
  ];

  return (
    <div className="container-fluid mt-3 mb-5 px-3 px-md-4">
      <Helmet>
        <title>Project - {project.name}</title>
      </Helmet>
      <div className="d-flex align-items-center mb-3">
        <span
          className="dst-icon-chevron-left p-2 medium-gray-background rounded me-2 clickable"
          onClick={(event: any) => completeNavigate(event, navigate, "/projects")}
        />
        <h1 className="mb-0 h3 me-3">{project.name}</h1>
      </div>
      <DstTabs
        tabsData={tabsData}
        variant="primary"
        activeKey={activeTab}
        onSelect={(key) => setActiveTab(key || "main-infos")}
      />

      <ToastContainer position="top-end" className="p-3">
        <Toast bg="success" show={showSuccessToast} onClose={() => setShowSuccessToast(false)} delay={3000} autohide>
          <Toast.Body className="text-white">
            {translate(language, "PAGES.PROJECT.MAIN_INFOS_TAB.EDIT_PROJECT.SUCCESS_UPDATE")}
          </Toast.Body>
        </Toast>
        <Toast
          bg="success"
          show={showCopyShortcutToast}
          onClose={() => setShowCopyShortcutToast(false)}
          delay={3000}
          autohide
        >
          <Toast.Body className="text-white">{translate(language, "PAGES.PROJECT.COPY_SHORTCUT_SUCCESS")}</Toast.Body>
        </Toast>
      </ToastContainer>
    </div>
  );
};

export default Project;
